if (!window.Eurus.loadedScript.has('custom-buybox.js')) {
  window.Eurus.loadedScript.add('custom-buybox.js');

  class CustomBuybox extends HTMLElement {
    constructor() {
      super();
      this.handleTierClick = this.handleTierClick.bind(this);
      this.handleTierKeydown = this.handleTierKeydown.bind(this);
      this.handleUpgradeClick = this.handleUpgradeClick.bind(this);
      this.handleAddToCart = this.handleAddToCart.bind(this);
    }

    connectedCallback() {
      const json = this.querySelector('[data-buybox-json]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);

      this.tierButtons = Array.from(this.querySelectorAll('[data-tier-index]'));
      this.giftChips = Array.from(this.querySelectorAll('[data-gift-id]'));
      this.progress = this.querySelector('[data-gifts-progress]');
      this.upgrade = this.querySelector('[data-upgrade]');
      this.upgradeText = this.querySelector('[data-upgrade-text]');
      this.atc = this.querySelector('[data-atc]');
      this.atcText = this.querySelector('[data-atc-text]');
      this.error = this.querySelector('[data-error]');
      this.upgradeTarget = null;

      this.tierButtons.forEach((button) => {
        button.addEventListener('click', this.handleTierClick);
        button.addEventListener('keydown', this.handleTierKeydown);
      });
      this.upgrade?.addEventListener('click', this.handleUpgradeClick);
      this.atc?.addEventListener('click', this.handleAddToCart);

      if (this.data.tiers.length) {
        this.selectTier(parseInt(this.dataset.defaultTier, 10) || 0);
      }
    }

    disconnectedCallback() {
      this.tierButtons?.forEach((button) => {
        button.removeEventListener('click', this.handleTierClick);
        button.removeEventListener('keydown', this.handleTierKeydown);
      });
      this.upgrade?.removeEventListener('click', this.handleUpgradeClick);
      this.atc?.removeEventListener('click', this.handleAddToCart);
    }

    handleTierClick(event) {
      this.selectTier(parseInt(event.currentTarget.dataset.tierIndex, 10));
    }

    handleTierKeydown(event) {
      const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
      if (!(event.key in keys)) return;
      event.preventDefault();
      const count = this.tierButtons.length;
      const next = (this.selected + keys[event.key] + count) % count;
      this.selectTier(next);
      this.tierButtons[next].focus();
    }

    handleUpgradeClick() {
      if (this.upgradeTarget === null) return;
      this.selectTier(this.upgradeTarget);
      this.tierButtons[this.upgradeTarget]?.focus();
    }

    selectTier(index) {
      const tier = this.data.tiers[index];
      if (!tier) return;
      this.selected = index;

      this.tierButtons.forEach((button, i) => {
        const isSelected = i === index;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-checked', isSelected);
        button.tabIndex = isSelected ? 0 : -1;
      });

      const unlockedIds = tier.gifts.map((gift) => gift.productId);
      this.giftChips.forEach((chip) => {
        chip.classList.toggle('is-locked', !unlockedIds.includes(parseInt(chip.dataset.giftId, 10)));
      });

      if (this.progress) {
        this.progress.textContent = this.data.progressText
          .replace('[unlocked]', tier.unlocked)
          .replace('[total]', this.data.giftTotal);
      }

      this.updateUpgrade(index, unlockedIds);
      this.updateCta(tier);
      this.hideError();
    }

    // Points to the next tier that unlocks gifts the selected tier does not include.
    updateUpgrade(index, unlockedIds) {
      if (!this.upgrade) return;
      this.upgradeTarget = null;

      for (let i = index + 1; i < this.data.tiers.length; i++) {
        const newGifts = this.data.tiers[i].gifts.filter((gift) => !unlockedIds.includes(gift.productId));
        if (newGifts.length) {
          this.upgradeTarget = i;
          this.upgradeText.textContent = this.data.upgradeText
            .replace('[tier]', this.data.tiers[i].name)
            .replace('[gifts]', newGifts.map((gift) => gift.title).join(' & '));
          break;
        }
      }

      this.upgrade.hidden = this.upgradeTarget === null;
    }

    updateCta(tier) {
      if (!this.atc) return;
      if (!this.data.available) {
        this.atc.disabled = true;
        this.atcText.textContent = this.data.soldOutText;
        return;
      }
      this.atcText.textContent = this.data.ctaText.replace('[tier]', tier.name).replace('[price]', tier.price);
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || this.data.errorText;
      this.error.hidden = false;
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }

    getSections() {
      const helper = window.Alpine?.store('xCartHelper');
      return helper ? helper.getSectionsToRender().map((section) => section.id) : [];
    }

    async postJSON(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      return data;
    }

    // Adds the coupon on top of the codes already in the cart; returns the cart with re-rendered sections.
    async applyCoupon(root, code, sections) {
      const cart = await fetch(`${root}cart.js`).then((response) => response.json());
      const codes = (cart.discount_codes || []).map((discount) => discount.code);
      if (!codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) codes.push(code);

      const updated = await this.postJSON(`${root}cart/update.js`, { discount: codes.join(','), sections });

      const couponStore = window.Alpine?.store('xCouponCodeDetail');
      if (couponStore) {
        couponStore.appliedDiscountCodes = (updated.discount_codes || [])
          .filter((discount) => discount.applicable)
          .map((discount) => discount.code);
        document.cookie = `eurus_discount_code=${couponStore.appliedDiscountCodes}; path=/`;
        document.dispatchEvent(new CustomEvent('eurus:cart:discount-code:change'));
      }
      return updated;
    }

    async handleAddToCart() {
      const tier = this.data.tiers[this.selected];
      if (!tier || !this.data.variantId || this.atc.disabled) return;

      const root = window.Shopify?.routes?.root || '/';
      const sections = this.getSections();
      // Gifts are nested under the main line via parent_id; the main line lists them as FREE.
      const gifts = tier.gifts.filter((gift) => gift.available);
      const main = {
        id: this.data.variantId,
        quantity: tier.quantity,
        properties: {
          [this.data.limitedEditionProperty]: this.data.limitedEditionValue,
         // ...Object.fromEntries(gifts.map((gift) => [gift.title, 'FREE']))
        }
      };
      const items = [
        main,
        ...gifts.map((gift) => ({ id: gift.variantId, quantity: 1, parent_id: this.data.variantId }))
      ];

      this.hideError();
      this.atc.disabled = true;
      this.atc.setAttribute('aria-busy', 'true');

      try {
        let response = await this.postJSON(`${root}cart/add.js`, { items, sections });
        if (tier.coupon) {
          response = await this.applyCoupon(root, tier.coupon, sections);
        }
        this.refreshCart(response.sections);
      } catch (error) {
        console.error('Custom buy box:', error);
        this.showError(error.message);
      } finally {
        this.atc.disabled = false;
        this.atc.removeAttribute('aria-busy');
      }
    }

    refreshCart(sections) {
      const Alpine = window.Alpine;
      if (!Alpine?.store('xCartHelper') || !Alpine.store('xMiniCart')) {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        return;
      }

      if (sections) {
        Alpine.store('xCartHelper').reRenderSections(sections);
      } else {
        Alpine.store('xMiniCart').reLoad();
      }
      Alpine.store('xMiniCart').openCart();

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) Alpine.store('xCartHelper').currentItemCount = parseInt(bubble.innerHTML, 10);
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
    }
  }

  if (!customElements.get('custom-buybox')) {
    customElements.define('custom-buybox', CustomBuybox);
  }
}
